import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enUS } from 'date-fns/locale/en-US';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '../../stores/organization.store';
import { useHseWeeklyStore } from '../../stores/hse-weekly.store';
import { BranchSiteSelect } from '../../components/data-entry/BranchMonthToolbar';
import HseWeeklyReportTable from '../../components/hse/HseWeeklyReportTable';
import ReportingWeekPicker from '../../components/hse/ReportingWeekPicker';
import PageHeader from '../../components/PageHeader';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import { HELP_GUIDE_HSE_TERMS, HELP_GUIDE_HSE_WEEKLY } from '../../constants/knowledgeBaseTryIt';
import { helpI18n } from '../../i18n/help';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../../utils/helpGuideSession';

type HseTourPhase = 'branch' | 'table' | 'save';

const HseWeeklyReport: React.FC = () => {
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization } = useOrganizationStore();
  const {
    sites,
    selectedSiteId,
    selectedWeekStart,
    draft,
    isLoadingSites,
    isLoadingReport,
    isSaving,
    error,
    successMessage,
    clearError,
    clearSuccess,
    loadPreferences,
    resetForOrganizationChange,
    fetchSites,
    setSelectedSiteId,
    setSelectedWeekStart,
    updateDraftRow,
    saveReport,
  } = useHseWeeklyStore();

  const [localError, setLocalError] = useState<string | null>(null);
  const [helpGuide, setHelpGuide] = useState<string | null>(null);
  const [hseTourPhase, setHseTourPhase] = useState<HseTourPhase>('branch');

  const showBranchTip = helpGuide === HELP_GUIDE_HSE_WEEKLY && hseTourPhase === 'branch';
  const showTableTip =
    (helpGuide === HELP_GUIDE_HSE_WEEKLY && hseTourPhase === 'table') ||
    helpGuide === HELP_GUIDE_HSE_TERMS;
  const showSaveTip = helpGuide === HELP_GUIDE_HSE_WEEKLY && hseTourPhase === 'save';
  const showHseTermsTip = helpGuide === HELP_GUIDE_HSE_TERMS;

  const finishHelpGuide = () => {
    setHelpGuide(null);
    setHseTourPhase('branch');
    clearHelpGuideActive();
  };

  const advanceHelpGuide = () => {
    if (helpGuide === HELP_GUIDE_HSE_TERMS) {
      finishHelpGuide();
      return;
    }
    if (hseTourPhase === 'branch') {
      setHseTourPhase('table');
      return;
    }
    if (hseTourPhase === 'table') {
      setHseTourPhase('save');
      return;
    }
    finishHelpGuide();
  };

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    // Accept legacy `hse-fill` as the weekly guide.
    if (
      guide !== HELP_GUIDE_HSE_WEEKLY &&
      guide !== 'hse-fill' &&
      guide !== HELP_GUIDE_HSE_TERMS
    ) {
      return;
    }
    if (guide === HELP_GUIDE_HSE_TERMS) {
      markHelpGuideActive(HELP_GUIDE_HSE_TERMS);
      setHelpGuide(HELP_GUIDE_HSE_TERMS);
      setHseTourPhase('table');
    } else {
      markHelpGuideActive(HELP_GUIDE_HSE_WEEKLY);
      setHelpGuide(HELP_GUIDE_HSE_WEEKLY);
      setHseTourPhase(guide === 'hse-fill' ? 'table' : 'branch');
    }
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!helpGuide) return;
    const id =
      hseTourPhase === 'branch'
        ? 'help-hse-branch'
        : hseTourPhase === 'table'
          ? 'help-hse-table'
          : 'help-hse-save';
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [helpGuide, hseTourPhase]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  useEffect(() => {
    if (!selectedOrganization?.id) return;
    resetForOrganizationChange();
    void fetchSites(selectedOrganization.id);
  }, [selectedOrganization?.id, resetForOrganizationChange, fetchSites]);

  const showBlockingLoader = (isLoadingSites && sites.length === 0) || isLoadingReport;

  const handleSave = useCallback(async () => {
    setLocalError(null);
    const ok = await saveReport();
    if (!ok) setLocalError(null);
  }, [saveReport]);

  const snackbarOpen = Boolean(error || localError || successMessage);
  const snackbarMessage = error || localError || successMessage || '';
  const snackbarSeverity = successMessage ? 'success' : 'error';

  const handleSnackbarClose = () => {
    clearError();
    clearSuccess();
    setLocalError(null);
  };

  if (!selectedOrganization) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {t('hse.title')}
        </Typography>
        <Alert severity="info">{t('hse.selectOrganization')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {(isLoadingSites || isLoadingReport) && <LinearProgress sx={{ mb: 2 }} />}

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
        <PageHeader
          icon={<HealthAndSafetyOutlinedIcon />}
          title={t('hse.title')}
          actions={
            <SetupCoachmark
              open={showBranchTip}
              title={tHelp('knowledgeBase.hseBranchGuideTitle')}
              subtitle={tHelp('knowledgeBase.hseBranchGuideSubtitle')}
              onDismiss={finishHelpGuide}
              onNext={advanceHelpGuide}
              nextLabel={t('selfSetup.tourNext')}
              placement="left"
            >
              <Box
                id="help-hse-branch"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  gap: 2,
                  rowGap: 1.5,
                  maxWidth: '100%',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}
              >
                <Box
                  sx={{
                    flex: '1 1 220px',
                    minWidth: 180,
                    maxWidth: 280,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <BranchSiteSelect
                    sites={sites}
                    selectedSiteId={selectedSiteId}
                    onSiteChange={setSelectedSiteId}
                    disabled={showBlockingLoader || isSaving}
                  />
                </Box>
                <Box
                  sx={{
                    flex: '1 1 220px',
                    minWidth: 180,
                    maxWidth: 280,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <ReportingWeekPicker
                    selectedWeekStart={selectedWeekStart}
                    onWeekChange={setSelectedWeekStart}
                    disabled={showBlockingLoader || isSaving}
                  />
                </Box>
              </Box>
            </SetupCoachmark>
          }
        />
      </LocalizationProvider>

      {sites.length === 0 && !isLoadingSites && (
        <Alert severity="warning" icon={<StorefrontOutlinedIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          {t('common.noBranchesForOrg')}
        </Alert>
      )}

      <Box id="help-hse-table">
        <SetupCoachmark
          open={showTableTip}
          title={
            showHseTermsTip
              ? tHelp('knowledgeBase.hseTermsGuideTitle')
              : tHelp('knowledgeBase.hseFillGuideTitle')
          }
          subtitle={
            showHseTermsTip
              ? tHelp('knowledgeBase.hseTermsGuideSubtitle')
              : tHelp('knowledgeBase.hseFillGuideSubtitle')
          }
          onDismiss={finishHelpGuide}
          onNext={advanceHelpGuide}
          nextLabel={
            showHseTermsTip ? t('selfSetup.tourDone') : t('selfSetup.tourNext')
          }
          fullWidth
        >
          <HseWeeklyReportTable
            draft={draft}
            onRowChange={updateDraftRow}
            disabled={!selectedSiteId || isSaving || isLoadingReport}
          />
        </SetupCoachmark>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <SetupCoachmark
          open={showSaveTip}
          title={tHelp('knowledgeBase.hseSaveGuideTitle')}
          subtitle={tHelp('knowledgeBase.hseSaveGuideSubtitle')}
          onDismiss={finishHelpGuide}
          onNext={finishHelpGuide}
          nextLabel={t('selfSetup.tourDone')}
          placement="left"
        >
          <Button
            id="help-hse-save"
            variant="contained"
            size="large"
            startIcon={<SaveOutlinedIcon />}
            onClick={() => void handleSave()}
            disabled={!selectedSiteId || isSaving || isLoadingReport}
          >
            {t('hse.saveButton')}
          </Button>
        </SetupCoachmark>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HseWeeklyReport;
